import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Stack,
  useTheme,
  Container,
  alpha,
} from "@mui/material";
import {
  SecurityOutlined, // Equivalent to Shield
  ArrowBack, // Equivalent to ArrowLeft
  EmailOutlined, // Equivalent to Mail
} from "@mui/icons-material";

const UnauthorizedPage = () => {
  const theme = useTheme();

  const handleGoBack = () => {
    window.history.back();
  };

  const handleContactSupport = () => {
    window.location.href = "mailto:support@company.com";
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        // Use your theme's background or a subtle gradient based on mode
        background:
          theme.palette.mode === "dark"
            ? theme.palette.background.default
            : `linear-gradient(135deg, ${theme.palette.neutral.light} 0%, #ffffff 100%)`,
        p: 2,
      }}
    >
      {/* --- DECORATIVE BLOBS (Background) --- */}
      <Box
        sx={{
          position: "absolute",
          top: "5%",
          left: "5%",
          width: 100,
          height: 100,
          borderRadius: "50%",
          bgcolor: theme.palette.primary.main,
          opacity: 0.1,
          filter: "blur(40px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "10%",
          right: "5%",
          width: 150,
          height: 150,
          borderRadius: "50%",
          bgcolor: theme.palette.secondary.main,
          opacity: 0.1,
          filter: "blur(60px)",
        }}
      />

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        {/* --- MAIN CARD --- */}
        <Paper
          elevation={theme.palette.mode === "dark" ? 4 : 10}
          sx={{
            p: { xs: 4, md: 6 },
            textAlign: "center",
            borderRadius: theme.appRadius.xl, // Using your custom radius
            bgcolor: "background.paper",
            // Add a subtle border in dark mode for definition
            border:
              theme.palette.mode === "dark"
                ? `1px solid ${theme.palette.divider}`
                : "none",
          }}
        >
          {/* --- ICON --- */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              // Use alpha to get that "primary-100" feel dynamically
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
            }}
          >
            <SecurityOutlined
              sx={{ fontSize: 40, color: theme.palette.primary.main }}
            />
          </Box>

          {/* --- STATUS PILL --- */}
          <Box sx={{ mb: 3 }}>
            <Chip
              label="401 Unauthorized"
              color="primary" // Uses your theme's primary color
              size="medium"
              sx={{
                fontWeight: 600,
                borderRadius: theme.appRadius.lg,
                height: 32,
              }}
            />
          </Box>

          {/* --- HEADING --- */}
          <Typography
            variant="h3" // Maps to your theme's font settings
            gutterBottom
            sx={{
              fontWeight: 700,
              color: "text.primary",
            }}
          >
            Access Restricted
          </Typography>

          {/* --- MESSAGE --- */}
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4, lineHeight: 1.6 }}
          >
            You don't have permission to access this resource. Please contact
            your administrator or try logging in with the appropriate
            credentials.
          </Typography>

          {/* --- ACTION BUTTONS --- */}
          <Stack spacing={2}>
            {/* Primary Action - Uses your custom 'gradient' variant */}
            <Button
              variant="gradient"
              color="primary" // or 'info'/'ocean' depending on preference
              fullWidth
              size="large"
              startIcon={<ArrowBack />}
              onClick={handleGoBack}
              sx={{
                py: 1.5,
                borderRadius: theme.appRadius.md,
                fontSize: theme.typography.button.fontSize,
              }}
            >
              Go Back
            </Button>

            {/* Secondary Action - Grey button style */}
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<EmailOutlined />}
              onClick={handleContactSupport}
              sx={{
                py: 1.5,
                borderRadius: theme.appRadius.md,
                // Mimic the "neutral-100" look from Tailwind
                bgcolor: theme.palette.neutral.light,
                color: theme.palette.text.primary,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: theme.palette.neutral.main, // Slightly darker grey on hover
                  boxShadow: "none",
                },
              }}
            >
              Contact Support
            </Button>
          </Stack>
        </Paper>

        {/* --- FOOTER --- */}
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Need help? Contact{" "}
            <Typography
              component="span"
              variant="body2"
              onClick={handleContactSupport}
              sx={{
                color: "primary.main",
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              support@company.com
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default UnauthorizedPage;
